import { mediaMax } from "components/designV2/breakpoints";
import { Radius, Spacing } from "components/designV2/constants";
import { ColorV2 } from "config/colors";
import { TypographyV2 } from "config/font";
import styled from "styled-components";
import { SettingsV2PageTitle } from "../companySettingsV2.styled";

export const DetailsContainer = styled.div`
  margin-bottom: 40px;
`;

export const ObSettingsHeader = styled.div`
  align-items: flex-start;
  display: flex;
  gap: ${Spacing.md}px;
  justify-content: space-between;
  margin-bottom: ${Spacing.xl}px;

  ${mediaMax.mobile} {
    flex-direction: column;
  }
`;

export const ObSettingsTitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.sm}px;
  min-width: 0;
`;

export const ObSettingsTitle = styled(SettingsV2PageTitle)``;

export const ObSettingsDescription = styled.div`
  ${TypographyV2.b2}
  color: ${ColorV2.Blue4};
  max-width: 640px;
`;

export const ObSettingsButtonSlot = styled.div`
  flex-shrink: 0;
  margin-left: auto;

  ${mediaMax.mobile} {
    margin-left: 0;
  }
`;

export const ObSectionTitle = styled.div`
  ${TypographyV2.b1}
  color: ${ColorV2.Blue1};
  margin-bottom: ${Spacing.md}px;
`;

export const ObSectionTitleOffset = styled(ObSectionTitle)`
  margin-top: ${Spacing.xl}px;
`;

export const ObSettingsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const ObSettingRow = styled.div`
  align-items: center;
  background: ${ColorV2.Blue7};
  border: 1px solid transparent;
  border-radius: ${Radius.xl}px;
  column-gap: 12px;
  display: grid;
  grid-template-columns:
    minmax(220px, 1.4fr) minmax(180px, 1fr) minmax(140px, 0.8fr)
    48px;
  padding: 12px 16px;
  width: 100%;

  ${mediaMax.tablet} {
    align-items: flex-start;
    grid-template-columns: 1fr auto;
    row-gap: ${Spacing.sm}px;
  }
`;

export const ObSettingCell = styled.div`
  min-width: 0;

  ${mediaMax.tablet} {
    grid-column: 1 / -1;
  }
`;

export const ObSettingActionsCell = styled.div`
  align-items: center;
  display: flex;
  justify-content: flex-end;
  min-height: 40px;

  ${mediaMax.tablet} {
    grid-column: 2;
    grid-row: 1;
  }
`;

export const ObIconButton = styled.button`
  align-items: center;
  background: transparent;
  border: none;
  cursor: pointer;
  display: inline-flex;
  height: 30px;
  justify-content: center;
  padding: 0;
  width: 30px;

  &:hover {
    opacity: 0.72;
  }

  &:focus-visible {
    outline: 2px solid ${ColorV2.Blue1};
    outline-offset: 2px;
  }
`;

export const ObFormLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.md}px;
`;

export const ObFormSection = styled.section`
  background: ${ColorV2.Blue7};
  border-radius: ${Radius.xl}px;
  display: flex;
  flex-direction: column;
  gap: ${Spacing.md}px;
  padding: ${Spacing.lg}px;
`;

export const ObDayGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${Spacing.sm}px;
  justify-content: center;
`;

export const ObDayOption = styled.button<{ $selected: boolean }>`
  ${TypographyV2.b2}
  background: ${(props) => (props.$selected ? ColorV2.Blue1 : ColorV2.Blue7)};
  border: 1.5px solid
    ${(props) => (props.$selected ? ColorV2.Blue1 : ColorV2.Blue5)};
  border-radius: 999px;
  color: ${(props) => (props.$selected ? ColorV2.White : ColorV2.Blue1)};
  cursor: pointer;
  min-height: 40px;
  padding: 0 ${Spacing.md}px;
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease,
    color 0.15s ease;

  &:hover {
    border-color: ${ColorV2.Blue1};
  }

  &:focus-visible {
    outline: 2px solid ${ColorV2.ElectricBlue};
    outline-offset: 2px;
  }
`;

export const ObSectionButtonSlot = styled.div`
  align-self: center;
`;

export const ObDateFieldSlot = styled.div`
  align-self: center;
  width: min(100%, 312px);
`;

export const ObTimeFields = styled.div`
  align-items: end;
  display: grid;
  gap: ${Spacing.md}px;
  grid-template-columns: auto auto auto;
  justify-content: flex-start;

  ${mediaMax.mobile} {
    align-items: stretch;
    grid-template-columns: 1fr;
  }
`;

export const ObTimeFieldSlot = styled.div`
  display: flex;
  justify-content: flex-start;
`;

export const ObTimeSeparator = styled.div`
  ${TypographyV2.h5}
  align-items: center;
  color: ${ColorV2.Blue4};
  display: inline-flex;
  justify-content: center;
  min-height: 40px;
  padding-bottom: ${Spacing.sm}px;

  ${mediaMax.mobile} {
    display: none;
  }
`;

export const ObFieldMessage = styled.div<{ $error?: boolean }>`
  ${TypographyV2.s1}
  color: ${(props) => (props.$error ? ColorV2.NegativeRed : ColorV2.Blue4)};
`;

export const ObSectionLabel = styled.div`
  ${TypographyV2.h5}
  color: ${ColorV2.Blue1};
`;

export const ObRadioGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${Spacing.md}px ${Spacing.lg}px;
  min-width: 0;
`;

export const ObRadioOption = styled.label`
  align-items: center;
  cursor: pointer;
  display: inline-flex;
  gap: ${Spacing.sm}px;
`;

export const ObHiddenRadio = styled.input`
  opacity: 0;
  pointer-events: none;
  position: absolute;
`;

export const ObRadioControl = styled.span`
  border: 2px solid ${ColorV2.Blue4};
  border-radius: 999px;
  display: inline-flex;
  flex-shrink: 0;
  height: 24px;
  position: relative;
  width: 24px;

  ${ObRadioOption} ${ObHiddenRadio}:checked + & {
    border-color: ${ColorV2.Blue1};
  }

  ${ObRadioOption} ${ObHiddenRadio}:checked + &::after {
    background: ${ColorV2.Blue1};
    border-radius: 999px;
    content: "";
    height: 12px;
    left: 50%;
    position: absolute;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 12px;
  }

  ${ObRadioOption} ${ObHiddenRadio}:focus-visible + & {
    outline: 2px solid ${ColorV2.Blue1};
    outline-offset: 3px;
  }
`;

export const ObRadioLabel = styled.span`
  ${TypographyV2.b2}
  color: ${ColorV2.Blue1};
`;

export const ObAmountRow = styled.div`
  align-items: end;
  display: flex;
  gap: ${Spacing.sm}px;
  justify-content: flex-start;
`;

export const ObAmountFieldSlot = styled.div`
  width: 240px;
`;

export const ObSummaryPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.md}px;
`;

export const ObSummaryRow = styled.div`
  align-items: start;
  display: grid;
  gap: ${Spacing.md}px;
  grid-template-columns: minmax(112px, auto) 1fr;

  ${mediaMax.mobile} {
    grid-template-columns: 1fr;
    gap: ${Spacing.xs}px;
  }
`;

export const ObSummaryLabel = styled.div`
  ${TypographyV2.b2}
  color: ${ColorV2.Blue4};
`;

export const ObSummaryValue = styled.div`
  ${TypographyV2.b1}
  color: ${ColorV2.Blue1};
  min-width: 0;
  word-break: break-word;
`;

export const ObModalActions = styled.div`
  align-items: center;
  display: flex;
  gap: ${Spacing.sm}px;
  justify-content: flex-end;

  ${mediaMax.mobile} {
    align-items: stretch;
    flex-direction: column-reverse;
  }
`;
